/**
 * Phase 27a — typed wrappers for the platform-admin surface mounted at
 * `/platform/*`. Every endpoint is gated on the `platform_admin` role
 * via `PlatformAdminGuard`; merchants logged into the dashboard with any
 * other role get 403. The synthetic Commerce OS platform org is the home
 * org for every platform_admin user (users.org_id is NOT NULL).
 */

import type { AxiosInstance } from 'axios';
import type {
  CreatePlatformOrgRequest,
  CreatePlatformOrgResponse,
  CreatePlatformOrgUserRequest,
  CreatePlatformOrgUserResponse,
  InitiatePasswordResetResponse,
  ListPlatformOrgsQuery,
  ListPlatformOrgsResponse,
  CreateSuiteGrantRequest,
  PlatformOrgDetailResponse,
  PlatformPaymentRoutingResponse,
  PlatformStatsResponse,
  PlatformSuiteGrant,
  PlatformSuiteGrantsResponse,
  SupportSessionTokens,
  UpdatePlatformOrgStatusRequest,
} from '@commerce-os/shared-types';

export async function listPlatformOrgs(
  client: AxiosInstance,
  query: ListPlatformOrgsQuery = {},
): Promise<ListPlatformOrgsResponse> {
  const { data } = await client.get<ListPlatformOrgsResponse>('/platform/orgs', {
    params: query,
  });
  return data;
}

export async function getPlatformOrg(
  client: AxiosInstance,
  id: string,
): Promise<PlatformOrgDetailResponse> {
  const { data } = await client.get<PlatformOrgDetailResponse>(
    `/platform/orgs/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function createPlatformOrg(
  client: AxiosInstance,
  body: CreatePlatformOrgRequest,
): Promise<CreatePlatformOrgResponse> {
  const { data } = await client.post<CreatePlatformOrgResponse>(
    '/platform/orgs',
    body,
  );
  return data;
}

export async function updatePlatformOrgStatus(
  client: AxiosInstance,
  id: string,
  body: UpdatePlatformOrgStatusRequest,
): Promise<PlatformOrgDetailResponse> {
  const { data } = await client.patch<PlatformOrgDetailResponse>(
    `/platform/orgs/${encodeURIComponent(id)}/status`,
    body,
  );
  return data;
}

export async function createPlatformOrgUser(
  client: AxiosInstance,
  orgId: string,
  body: CreatePlatformOrgUserRequest,
): Promise<CreatePlatformOrgUserResponse> {
  const { data } = await client.post<CreatePlatformOrgUserResponse>(
    `/platform/orgs/${encodeURIComponent(orgId)}/users`,
    body,
  );
  return data;
}

/**
 * Admin-triggered password reset for an existing user. Same email-link
 * flow the user would get from /forgot-password — the token is dispatched
 * to the merchant's email of record. Useful when an operator can't reach
 * their own inbox or has forgotten the recovery flow exists.
 */
export async function resetPlatformUserPassword(
  client: AxiosInstance,
  userId: string,
): Promise<InitiatePasswordResetResponse> {
  const { data } = await client.post<InitiatePasswordResetResponse>(
    `/platform/users/${encodeURIComponent(userId)}/password-reset`,
  );
  return data;
}

export async function getPlatformStats(
  client: AxiosInstance,
): Promise<PlatformStatsResponse> {
  const { data } = await client.get<PlatformStatsResponse>('/platform/stats');
  return data;
}

/**
 * Phase 66.0 — card payment routing across merchant orgs: any merchant
 * card money on the platform's own Stripe account (should be none), orgs
 * whose saved routing refuses card payments, and monthly card volume by
 * where it settled. `months` is 1–24 (default 12).
 */
export async function getPlatformPaymentRouting(
  client: AxiosInstance,
  months?: number,
): Promise<PlatformPaymentRoutingResponse> {
  const { data } = await client.get<PlatformPaymentRoutingResponse>('/platform/payments/routing', {
    params: months === undefined ? undefined : { months },
  });
  return data;
}

/**
 * Enter a merchant org as a platform admin (a 60-minute support session).
 * Returns tokens scoped to that org + the session; the merchant sees the
 * admin's changes as "CommerceOS Admin".
 */
export async function startSupportSession(
  client: AxiosInstance,
  orgId: string,
): Promise<SupportSessionTokens> {
  const { data } = await client.post<SupportSessionTokens>('/platform/support-sessions', { orgId });
  return data;
}

/** Leave the merchant org — tokens for the admin's home org (CommerceOS). */
export async function endSupportSession(client: AxiosInstance): Promise<SupportSessionTokens> {
  const { data } = await client.post<SupportSessionTokens>('/platform/support-sessions/end');
  return data;
}

// ── Suite comps (2026-09-30) ────────────────────────────────────────────────
// Open a suite the org's plan doesn't include, with a reason and an optional
// end date. The merchant sees it as included by CommerceOS.

/** The org's plan beside every comp it has had, newest first. */
export async function listPlatformSuiteGrants(
  client: AxiosInstance,
  orgId: string,
): Promise<PlatformSuiteGrantsResponse> {
  const { data } = await client.get<PlatformSuiteGrantsResponse>(
    `/platform/orgs/${encodeURIComponent(orgId)}/suite-grants`,
  );
  return data;
}

/** Comp a suite. A live comp for the same suite is replaced. */
export async function createPlatformSuiteGrant(
  client: AxiosInstance,
  orgId: string,
  body: CreateSuiteGrantRequest,
): Promise<PlatformSuiteGrant> {
  const { data } = await client.post<PlatformSuiteGrant>(
    `/platform/orgs/${encodeURIComponent(orgId)}/suite-grants`,
    body,
  );
  return data;
}

/** End a comp now. */
export async function endPlatformSuiteGrant(
  client: AxiosInstance,
  orgId: string,
  grantId: string,
): Promise<PlatformSuiteGrant> {
  const { data } = await client.delete<PlatformSuiteGrant>(
    `/platform/orgs/${encodeURIComponent(orgId)}/suite-grants/${encodeURIComponent(grantId)}`,
  );
  return data;
}
